import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { count } from "d3";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

function StringencyMultiples({
  stringencyData,
  incidentData,
  countries,
  labels,
  columns = 3,
}) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);
  let height = 900;

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    d3.select(".legend").selectChildren().remove();
    svg.selectAll("*").remove();
    if (!wrapDim) return;

    let width = wrapDim.width;

    if (width < 600) columns = 1;
    else if (width < 900) columns = 2;

    const smallWidth = width / (columns + 1);
    //const smallHeight = height / Math.ceil(countries.length / columns + 1);
    const smallHeight = 300;

    // let ticks = null;
    // if(smallWidth <= 300) ticks = 3;
    // console.log("string mult width is ", smallWidth);

    //do a check if there's stringency data for every country, otherwise remove
    var i = countries.length;
    while (i--) {
      const country = countries[i];
      const countryStringencyData = stringencyData
        .map((item) => {
          return { date: item.date, value: item[country] };
        })
        .filter((d) => d.value);

      if (countryStringencyData.length === 0) {
        countries.splice(i, 1);
      }
    }

    const rows = Math.ceil(countries.length / columns);

    height = (smallHeight + 60) * rows;

    svg.attr("height", height).attr("width", width);

    const relaxFactor = 1.1;
    const tooltipWidth = 240;
    const tooltipHeight = 240;

    // const lineColor = '#b1b1b1';
    // const lineColor = 'green';
    const colors = [
      "#1E2748",
      "#A89659",
      "#7EB3C6",
      "#684B7C",
      "#7E8491",
      // "#808492",
      "#60A17D",
    ];
    const curve = d3.curveCatmullRom;
    const stepCurve = d3.curveStepAfter;

    const mainGroup = svg.append("g").attr("transform", "translate(50,0)");

    var maxIncidents = 0;
    countries.forEach((country) => {
      const countryMaxIncidents = d3.max(incidentData, (d) => d[country]);

      maxIncidents =
        countryMaxIncidents > maxIncidents ? countryMaxIncidents : maxIncidents;
    });

    const dateExtent = d3.extent(incidentData, (d) => d.date);

    var startDate = new Date(dateExtent[0].getTime());
    startDate.setDate(startDate.getDate() - 15);

    var endDate = new Date(dateExtent[1].getTime());
    endDate.setDate(endDate.getDate() + 15);

    const x = d3
      .scaleUtc()
      .domain([startDate, endDate])
      .range([0, smallWidth])
      .clamp(true);

    const xAxis = (g) =>
      g.attr("transform", `translate(0,${smallHeight})`).call(
        d3
          .axisBottom(x)
          // .ticks(ticks)
          .tickFormat((d) => {
            let formatString = "%b";
            if (d.getMonth() === 0) formatString = "'%y";
            return d3.timeFormat(formatString)(d);
          })
          .tickSizeOuter(0)
      );

    const yRight = d3.scaleLinear().domain([0, 100]).range([smallHeight, 30]);

    const yAxisRight = (g) =>
      g.attr("transform", `translate(${smallWidth},0)`).call(
        d3
          .axisRight(yRight)
          .ticks(5)
          .tickSizeOuter(0)
          .tickFormat((d) => d3.format(".0%")(d / 100))
      );

    const yLeft = d3
      .scaleLinear()
      .domain([0, maxIncidents * relaxFactor])
      .range([smallHeight, 30]);

    const yAxisLeft = (g) =>
      g
        .attr("transform", `translate(0,0)`)
        .call(d3.axisLeft(yLeft).ticks(5).tickSizeOuter(0));

    const highlightArea = d3
      .area()
      .x((d) => x(d.date))
      .y0(yLeft(0))
      .y1((d) => yLeft(d.value))
      .curve(curve);

    const stepArea = d3
      .area()
      .x((d) => x(d.date))
      .y0(yRight(0))
      .y1((d) => yRight(d.value))
      .curve(stepCurve);

    const stepLine = d3
      .line()
      // .defined(d => d.value)
      .x((d) => x(d.date))
      .y((d) => yRight(d.value))
      .curve(stepCurve);

    const line = d3
      .line()
      // .defined(d => d.value)
      .x((d) => x(d.date))
      .y((d) => yLeft(d.value))
      .curve(curve);

    const legendDiv = d3.select(".legend");

    const legendEntry = legendDiv
      .selectAll(".legend-row")
      .data(["Stringency level", "Incidents"])
      .enter()
      .append("div")
      .attr("class", (d, i) => `legend-row legend-row-${i}`);

    legendEntry
      .append("div")
      .attr("class", "legend-rect")
      .style("background", (d, i) => (i == 0 ? colors[2] : colors[3]))
      .classed("rect-line", (d, i) => (i == 0 ? false : true));

    legendEntry
      .append("span")
      .attr("class", (d, i) => `legend-text legend-text-${i}`)
      .attr("content", (d) => d)
      .text((d) => d);

    // console.log("going in with ", smallHeight, smallWidth);
    countries.forEach((d, index) => {
      var row = Math.floor(index / columns);
      var col = index % columns;
      const countryCanvas = mainGroup
        .append("g")
        .attr(
          "transform",
          `translate(${col * (smallWidth + smallWidth / 3)},${
            row * (smallHeight + 70) + 30
          })`
        );

      countryCanvas.append("g").call(xAxis);
      countryCanvas.append("g").call(yAxisRight);
      countryCanvas.append("g").call(yAxisLeft);

      countryCanvas
        .append("text")
        .attr("x", smallWidth / 2)
        .attr("y", 15)
        .attr("class", "stringency-country-label")
        .text(d);

      const countryIncidentData = incidentData.map((item) => {
        return { date: item.date, value: item[d] };
      });

      const countryStringencyData = stringencyData
        .map((item) => {
          return { date: item.date, value: item[d] };
        })
        .filter((d) => d.value)
        .sort((a, b) => a.date - b.date);

      countryStringencyData.unshift({
        date: countryIncidentData[0].date,
        value: countryStringencyData[0].value,
      });

      countryStringencyData.push({
        date: countryIncidentData[countryIncidentData.length - 1].date,
        value: countryStringencyData[countryStringencyData.length - 1].value,
      });

      countryCanvas
        .append("path")
        .attr("class", `area area-country-${index}`)
        .attr("fill", colors[2])
        .attr("opacity", 0.5)
        // .attr("d", highlightArea(countryIncidentData));
        .attr("d", stepArea(countryStringencyData));

      countryCanvas
        .append("path")
        .attr("class", `step country-step-${index}`)
        .attr("fill", "none")
        .attr("stroke", colors[3])
        .style("opacity", 1)
        .attr("stroke-width", 2)
        // .attr("d", stepLine(countryStringencyData));
        .attr("d", line(countryIncidentData));

      const annotationsGroup = countryCanvas
        .append("g")
        .attr("class", "annotations-group");

      const flagLine = annotationsGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", 0)
        .attr("stroke", "#b1b1b1")
        .attr("stroke-width", 1)
        .attr("opacity", 0);

      countryCanvas
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", smallWidth)
        .attr("height", smallHeight)
        .attr("fill", "red")
        .attr("opacity", 0)
        .attr("class", "mouseover")
        .on("mousemove", (event) => {
          svg.selectAll(".svg-tooltip-stringency").remove();
          // flagLine.attr("opacity", 0);
          const dates = incidentData.map((d) => d.date);
          const stringencyDates = countryStringencyData.map((d) => d.date);
          var mouse = d3.pointer(event);

          const xm = x.invert(mouse[0]).getTime();
          const i1 = d3.bisectLeft(dates, xm, 1);
          const i0 = i1 - 1;
          const i = xm - dates[i0] > dates[i1] - xm ? i1 : i0;

          const j1 = d3.bisectLeft(stringencyDates, xm, 1);
          const j0 = j1 - 1;
          const j =
            xm - stringencyDates[j0] > stringencyDates[j1] - xm ? j1 : j0;

          if (i >= 0 && i < incidentData.length) {
            d3.selectAll(".hover-group").remove();

            const hoverGroup = countryCanvas
              .append("g")
              .attr("class", `hover-group hover-group-${index}`);

            hoverGroup
              .append("circle")
              .attr("fill", colors[3])
              .attr("stroke", "white")
              .attr("stroke-width", 1)
              .attr("r", 4)
              .attr("cx", x(countryIncidentData[i].date))
              .attr("cy", yLeft(countryIncidentData[i].value));

            const incidentText = hoverGroup
              .append("g")
              .attr("transform", `translate(0,15)`);

            const incidentsString = d3.format(",.0f")(
              countryIncidentData[i].value
            );
            const stringencyString = d3.format(",.0f")(
              countryStringencyData[j].value
            );
            const dateString = d3.timeFormat("%b %Y")(incidentData[i].date);

            // incidentText
            //   .append("text")
            //   .attr("x", smallWidth / 2)
            //   .attr("text-anchor", "middle")
            //   .attr("font-size", 12)
            //   .text(
            //     `${dateString}: ${incidentsString} incidents (${stringencyString} stringency)`
            //   );

            var xBaseline = x(countryIncidentData[i].date);
            var yBaseline = yLeft(countryIncidentData[i].value);
            const flagSize = 150;
            flagLine
              .attr("opacity", 1)
              .attr("x1", xBaseline)
              .attr("x2", xBaseline)
              .attr("y1", yBaseline)
              .attr("y2", yBaseline - flagSize);

            var fObject = annotationsGroup
              .append("foreignObject")
              .attr("class", "svg-tooltip-stringency")
              // .attr('opacity', .7)
              // .style('background-color', 'rgba(255,255,255,.5)')
              .attr("x", () => {
                let offset = xBaseline;
                if (offset >= smallWidth / 2) offset -= 162;
                return offset;
              })
              .attr("y", yBaseline - flagSize)
              .attr("width", tooltipWidth)
              .attr("height", tooltipHeight);

            var fo = fObject
              .append("xhtml:body")
              .attr("xmlns", "http://www.w3.org/1999/xhtml");

            var h1 = fo.append("xhtml:h1");
            h1.attr("class", "h1").html(
              d3.timeFormat("%b %Y")(incidentData[i].date)
            );

            fo.append("xhtml:hr");

            // var div = fo.append("xhtml:div");
            var firstLine = fo.append("xhtml:div");
            firstLine
              .append("xhtml:span")
              .attr("class", "highlight-value")
              .html(incidentsString);
            firstLine
              .append("xhtml:span")
              .attr("class", "highlight-desc")
              .html("incidents");

            // var div = fo.append("xhtml:div");
            var secondLine = fo
              .append("xhtml:div")
              .attr("class", "highlight-div");
            secondLine
              .append("xhtml:span")
              .attr("class", "highlight-value")
              .html(`${stringencyString}%`);
            secondLine
              .append("xhtml:span")
              .attr("class", "highlight-desc")
              .html("stringency");
          }
        })
        .on("mouseout", (event) => {
          svg.selectAll(".svg-tooltip-stringency").remove();
          flagLine.attr("opacity", 0);
        });
    });
  }, [wrapDim]);

  return (
    <div>
      <div className="controls">
        <div className="legend"></div>
      </div>

      <div ref={wrapperRef} className="matrix-wrapper">
        <div className="chartWrapper">
          <svg ref={svgRef}></svg>
        </div>
      </div>
    </div>
  );
}

export default StringencyMultiples;
